import { DroneIcon, GroupIcon, SirenIcon, WarningIcon } from "@/assets/icons/Icons";
import { useAlertsPanel } from "@/context/AlertsPanelContext";
import { useCircleSelection } from "@/context/CircleSelectionContext";
import { useGroup } from "@/context/GroupContext";
import { useIncidents } from "@/context/IncidentsContext";
import { useSelectedAlert } from "@/context/SelectedAlertContext";
import { useAppDispatch, useAppSelector } from "@/redux/dispatch";
import { RootState } from "@/redux/store/rootReducers";
import { Incident, MapIncident } from "@/types/incidents";
import { formatRelativeTime } from "@/utils/formatTime";
import { formatUserInfo } from "@/utils/formatUserInfo";
import { getIncidentLocation } from "@/utils/locationUtils";
import { Group, MapPin, MessageSquareText, RotateCw, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../ui/loading-spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { setSelectedIncident, setSelectedIncidentGroupId, setSelectedTrackingId } from "@/redux/slice/incidentSlice";
import { useAuth } from "@/context/AuthContext";

interface AlertCardProps {
  alert: Incident | MapIncident;
  isSelected: boolean;
  onAlertClick: (alert: Incident | MapIncident) => void;
  onChatClick: (alert: Incident | MapIncident) => void;
  isGrouped?: boolean;
  groupStats?: {
    nearDangerCount: number;
    dangerCount: number;
    droneCount: number;
    totalAlerts: number;
  };
  hideMessageButton?: boolean;
  hideReportedTime?: boolean;
}

export const AlertCard = ({
  alert,
  isSelected,
  onAlertClick,
  onChatClick,
  isGrouped,
  groupStats,
  hideMessageButton = false,
  hideReportedTime = false,
}: AlertCardProps) => {
  const { userDetails } = useAuth();

  const getAlertConfig = () => {
    if (isGrouped) {
      return {
        color: "bg-[#FF9500]",
        icon: <GroupIcon />,
      };
    }

    let color = "bg-[#FF3B30]";
    let icon = <SirenIcon />;

    if (alert.incidentType === "NEAR_DANGER") {
      color = "bg-[#FFCC00]";
      icon = <WarningIcon />;
    } else if (alert.incidentType === "DRONE_ALERT") {
      color = "bg-[#26292C]";
      icon = <DroneIcon />;
    }

    return { color, icon };
  };

  const getSituationText = () => {
    if (alert.situation) return alert.situation;

    switch (alert.incidentType) {
      case "NEAR_DANGER":
        return "Near Danger";
      case "DANGER":
        return "Danger";
      case "DRONE_ALERT":
        return "Drone Alert";
      default:
        return alert.incidentType;
    }
  };

  const config = getAlertConfig();
  const situationText = getSituationText();

  return (
    <div
      key={alert.incidentID}
      className={`w-full sm:w-[388px] pt-4 px-4 pb-2 border-[1px] rounded-[8px] 
                hover:bg-gray-50 dark:hover:bg-dark transition-all duration-300 relative
                ${isSelected ? "border-[#959697] shadow-[0px_2px_4px_0px_#00000026] bg-[#EEEEEE] dark:bg-dark" : "border-[#E3E3E3]"}`}
    >
      <button
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-0"
        onClick={() => onAlertClick(alert)}
        aria-label={`Open details for alert ${alert.incidentID}`}
      />
      <div className="flex items-center gap-4 relative z-10 pointer-events-none">
        <div
          className={`w-[40px] h-[40px] rounded-full flex items-center justify-center ${config.color} text-white`}
        >
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center mb-1">
            <div className="text-body-bold truncate dark:text-gray-200">
              {isGrouped
                ? `Group #${alert.groupID}`
                : `Case ID #${alert.incidentID}`}
            </div>
            {!isGrouped && userDetails?.userType === 'Administrator' &&
              <span className="text-[#05539c] flex items-center">
                <MapPin size={18} color="#05539c" />{alert.locationID}
              </span>
            }
          </div>
          <div className="flex items-center min-w-0">
            {isGrouped && groupStats ? (
              <>
                <div className="text-overline-bold shrink-0 dark:text-gray-200">
                  {groupStats.totalAlerts} Users</div>
                <div className="text-overline dark:text-gray-200 truncate whitespace-nowrap overflow-hidden text-ellipsis">
                  {groupStats.nearDangerCount > 0 && (
                    <>,&nbsp;{groupStats.nearDangerCount} Near danger</>
                  )}
                  {groupStats.dangerCount > 0 && (
                    <>,&nbsp;{groupStats.dangerCount} Danger</>
                  )}
                  {groupStats.droneCount > 0 && (
                    <>,&nbsp;{groupStats.droneCount} Drone Alert</>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-wrap items-start break-words whitespace-normal dark:text-gray-200 min-w-0">
                <span className="text-overline-bold">
                  {formatUserInfo(alert.ReportedByUser)} | &nbsp;
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-overline cursor-pointer">
                        {situationText}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        {alert.situation || alert.incidentType}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </div>
        {!hideMessageButton && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Open Chat"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChatClick(alert);
                  }}
                  className="p-2 hover:bg-blue-50 dark:hover:bg-dark rounded-full transition-colsors duration-200 group relative pointer-events-auto"
                >
                  {alert?.isUnreadMessage &&
                    <span className="absolute top-1 right-1 w-3 h-3 bg-red-400 rounded-full" />
                  }

                  <MessageSquareText className="dark:text-gray-200" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Open Chat</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {!hideReportedTime && (
        <div className="flex items-center pt-2 justify-end gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-gray-600 dark:text-gray-200 italic">
                  Reported {formatRelativeTime(alert.reportedOn)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  {new Date(alert.reportedOn).toLocaleString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
};

const AlertsPanel = () => {
  const { isAlertsPanelOpen, setIsAlertsPanelOpen } = useAlertsPanel();
  const navigate = useNavigate();
  const { incidents: alerts, isLoading, fetchIncidents } = useIncidents();
  const { selectedAlertId, setSelectedAlertId } = useSelectedAlert();
  const { setCircleSelectionMode, setIsGroupingPanelOpen } =
    useCircleSelection();
  const { groupedAlerts, groupStats } = useGroup();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { selectedIncidentGroupID } = useAppSelector((state: RootState) => state.incidents)
  const dispatch = useAppDispatch();

  const handleRefreshClick = () => {
    try {
      setIsRefreshing(true);
      fetchIncidents();
    } catch (error) {
      console.error("Failed to refresh incidents:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAlertClick = (alert: Incident | MapIncident) => {
    const coordinates = getIncidentLocation(alert);
    if (coordinates) {
      setSelectedAlertId(alert.incidentID);
      dispatch(setSelectedIncident(alert));
      dispatch(setSelectedTrackingId(alert.incidentID));
      dispatch(setSelectedIncidentGroupId((alert.grouped ? alert.groupID : "")));
    } else {
      console.warn("No valid location data for this alert");
    }
    // }
  };

  const handleChatClick = (alert: Incident | MapIncident) => {
    navigate(`/alerts/chat/${alert.incidentID}`);
    dispatch(setSelectedTrackingId(alert.incidentID))
    dispatch(setSelectedIncident(alert));
    dispatch(setSelectedIncidentGroupId((alert.grouped ? alert.groupID : "")));
  };

  const handleGroupClick = () => {
    setCircleSelectionMode(true);
    setIsGroupingPanelOpen(true);
  };

  const renderAlertCards = () => {
    const groupedCards = Object.entries(groupedAlerts)
      .map(([groupId]) => {
        const stats = groupStats[groupId];
        if (!stats) return null;

        const latestAlert = stats.latestAlert;
        const coordinates = getIncidentLocation(latestAlert);
        if (!coordinates) return null;
        return (
          <AlertCard
            key={groupId}
            alert={latestAlert}
            isSelected={selectedAlertId === latestAlert.incidentID || selectedIncidentGroupID === latestAlert.groupID}
            onAlertClick={handleAlertClick}
            onChatClick={handleChatClick}
            isGrouped={true}
            groupStats={stats}
          />
        );
      })
      .filter(Boolean);

    const ungroupedCards = alerts
      .filter((alert) => !alert.grouped)
      .map((alert) => (
        <AlertCard
          key={alert.incidentID}
          alert={alert}
          isSelected={selectedAlertId === alert.incidentID}
          onAlertClick={handleAlertClick}
          onChatClick={handleChatClick}
        />
      ));
    if (groupedCards.length === 0 && ungroupedCards.length === 0) {
      return (
        <div className="flex justify-center items-center h-full">
          <div className="text-center text-sm text-gray-600 dark:text-gray-200">
            Everything is quiet here.
          </div>
        </div>
      );
    }
    return [...groupedCards, ...ungroupedCards];
  };

  if (!isAlertsPanelOpen) return null;

  return (
    <div className="fixed top-[60px] md:top-[60px] lg:top-0 w-full sm:w-[440px] h-full bg-white dark:bg-black shadow-lg pl-0 ml-0 sm:pl-4 sm:-ml-4">
      <div className="h-[60px] md:h-[70px] sm:h-[80px] px-6 py-4 flex items-center justify-between border-b border-[#E3E3E3]">
        <div className="text-sub-heading dark:text-gray-200">Alerts</div>
        <div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Create Group"
                  onClick={handleGroupClick}
                  className="p-2 hover:bg-gray-50 dark:hover:bg-dark rounded-full transition-colors duration-200"
                >
                  <Group size="24px" className="text-[#959697] dark:text-gray-200" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Create Group</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Refresh"
                  onClick={handleRefreshClick}
                  disabled={isRefreshing}
                  className={`p-2 rounded-full transition-colors duration-200 
                  ${isRefreshing ? "cursor-not-allowed opacity-50" : "hover:bg-gray-50 dark:hover:bg-dark"}`}
                >
                  {isRefreshing ? (
                    <LoadingSpinner size="small" color="rgb(5 83 156)" />
                  ) : (
                    <RotateCw size={"24px"} className="text-[#959697] dark:text-gray-200" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Refresh</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Close"
                  onClick={() => {
                    setIsAlertsPanelOpen(false);
                    setSelectedAlertId(null);
                    dispatch(setSelectedIncident(null));
                    dispatch(setSelectedIncidentGroupId(""));
                    navigate("/map");
                  }}
                  className="p-2 hover:bg-gray-50 dark:hover:bg-dark rounded-full transition-colors duration-200"
                >
                  <X size={"24px"} className="text-[#959697] dark:text-gray-200" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Close</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <LoadingSpinner size="medium" color="rgb(5 83 156)" />
        </div>
      ) : (
        <div className="h-[calc(100%-80px)] overflow-y-auto">
          <div className="p-4 space-y-2">{renderAlertCards()}</div>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;
